// src/api/axiosConfig.js
import axios from "axios";
import { trackBusinessEventFromAxiosResponse } from "../analytics/networkBusinessEvents";
import {
  createRequestTelemetryMeta,
  trackApiTelemetryFromAxiosError,
  trackApiTelemetryFromAxiosResponse,
} from "../analytics/networkTelemetry";

// In production (Vercel), use /api so requests go through the Vercel rewrite proxy
// (same-origin → Safari ITP doesn't block cookies). In dev, hit localhost directly.
const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const CSRF_COOKIE_NAME = "xphub_csrf";
let inMemoryCsrfToken = "";

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line no-console
  console.log("API_BASE_URL:", API_BASE_URL);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,              // cookies httpOnly si las usas
  headers: { "Content-Type": "application/json" },
  timeout: 120000,
});

function emitAppEvent(name, detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function requestPath(config = {}) {
  const rawUrl = String(config.url || "");
  if (!rawUrl) return "";
  try {
    const absolute = new URL(rawUrl, API_BASE_URL);
    return `${absolute.pathname}${absolute.search}`;
  } catch {
    return rawUrl;
  }
}

function requestMethod(config = {}) {
  return String(config.method || "get").trim().toLowerCase();
}

function isMutatingRequest(config = {}) {
  return ["post", "put", "patch", "delete"].includes(requestMethod(config));
}

function requestSummary(config = {}) {
  const method = requestMethod(config).toUpperCase();
  const path = requestPath(config) || "/api";
  return `${method} ${path}`;
}

function apiErrorContext(error) {
  const status = Number(error?.response?.status || 0);
  const config = error?.config || {};
  const requestId =
    error?.response?.headers?.["x-request-id"] ||
    error?.response?.data?.requestId ||
    "";

  let kind = "http_error";
  if (!status) kind = error?.code === "ECONNABORTED" ? "timeout" : "network_or_cors";

  return {
    kind,
    method: requestMethod(config).toUpperCase(),
    path: requestPath(config),
    summary: requestSummary(config),
    status,
    code: error?.response?.data?.error || error?.code || "",
    requestId: String(requestId || ""),
  };
}

function rememberApiError(context) {
  if (typeof window === "undefined" || !context) return;

  const entry = {
    ...context,
    at: new Date().toISOString(),
  };

  const previous = Array.isArray(window.__XPHUB_LAST_API_ERRORS__)
    ? window.__XPHUB_LAST_API_ERRORS__
    : [];
  const next = [entry, ...previous].slice(0, 10);
  window.__XPHUB_LAST_API_ERRORS__ = next;

  try {
    sessionStorage.setItem("xphub:last_api_errors", JSON.stringify(next));
  } catch {
    // noop: diagnóstico local, nunca debe romper la app.
  }
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const needle = `${name}=`;
  const parts = String(document.cookie || "").split(";").map((part) => part.trim());
  const pair = parts.find((part) => part.startsWith(needle));
  if (!pair) return "";
  return decodeURIComponent(pair.slice(needle.length));
}

function resolveCsrfToken() {
  const cookieToken = readCookie(CSRF_COOKIE_NAME);
  if (cookieToken) {
    inMemoryCsrfToken = cookieToken;
    return cookieToken;
  }
  return inMemoryCsrfToken;
}

function shouldRefreshProgress(config = {}) {
  if (!isMutatingRequest(config) || config.skipProgressRefresh) return false;
  const path = requestPath(config);
  return [
    "/library",
    "/reviews",
    "/ratings",
    "/comments",
    "/lists",
    "/game-sessions",
    "/steam",
    "/friends",
    "/users/",
    "/follow",
    "/user/favorites",
  ].some((prefix) => path.startsWith(prefix) || path.includes(prefix));
}

function shouldShowGlobalApiError(error) {
  const config = error?.config || {};
  if (config.silentGlobalError || config.skipGlobalErrorToast) return false;
  const status = Number(error?.response?.status || 0);
  if (status === 401 || status === 403 || status === 404) return false;
  if (!status) return true;
  if (status >= 500) return true;
  return isMutatingRequest(config) && (status === 408 || status === 409 || status === 413 || status === 429);
}

function apiErrorMessage(error, context = apiErrorContext(error)) {
  const status = Number(error?.response?.status || 0);
  const serverMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    "";
  if (serverMessage && status < 500) return String(serverMessage);
  if (!status) {
    if (context?.kind === "timeout") {
      return `El servidor tardó demasiado en responder. Origen: ${context.summary}.`;
    }
    return `No se recibió respuesta del servidor. Origen: ${context?.summary || "API"}.`;
  }
  if (status === 413) return "El archivo o la petición excede el tamaño permitido.";
  if (status === 429) return "Has hecho demasiadas acciones seguidas. Espera un momento.";
  return "Ha fallado el servidor. Inténtalo de nuevo en unos segundos.";
}

api.interceptors.request.use((config) => {
  config.metadata = {
    ...(config.metadata || {}),
    ...createRequestTelemetryMeta(),
  };

  // Auth is handled by the httpOnly xphub_at cookie (withCredentials: true above).
  // No Bearer token injected from localStorage — cookie-only auth for this instance.

  if (isMutatingRequest(config) && !config.headers["X-CSRF-Token"]) {
    const csrfToken = resolveCsrfToken();
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }

  try { localStorage.setItem("lastActivityAt", String(Date.now())); } catch {}
  return config;
});

api.interceptors.response.use(
  (response) => {
    try {
      trackBusinessEventFromAxiosResponse(response);
    } catch {
      // noop: analytics nunca debe romper flujo funcional
    }
    const csrfToken = String(response?.data?.csrfToken || "").trim();
    if (csrfToken) {
      inMemoryCsrfToken = csrfToken;
    }
    try {
      trackApiTelemetryFromAxiosResponse(response);
    } catch {
      // noop: telemetry nunca debe romper flujo funcional
    }
    if (shouldRefreshProgress(response.config)) {
      emitAppEvent("xphub:progress-refresh-request", {
        reason: "api-mutation",
        path: requestPath(response.config),
      });
    }
    return response;
  },
  async (error) => {
    const context = apiErrorContext(error);

    // Auto-retry para errores de red (status 0) en GETs — Render cold start
    const config = error?.config || {};
    const isNetworkError = !error?.response && context?.kind !== "timeout";
    const isGet = requestMethod(config) === "get";
    const retryCount = Number(config.__retryCount || 0);
    if (isNetworkError && isGet && retryCount < 2 && !config.__noRetry) {
      config.__retryCount = retryCount + 1;
      await new Promise((resolve) => setTimeout(resolve, 1200 * config.__retryCount));
      return api(config);
    }

    rememberApiError(context);
    emitAppEvent("xphub:api-error", context);
    if (!error?.response) {
      // eslint-disable-next-line no-console
      console.warn("[XPHub API] request failed without HTTP response", context);
    }
    try {
      trackApiTelemetryFromAxiosError(error);
    } catch {
      // noop: telemetry nunca debe romper flujo funcional
    }
    if (shouldShowGlobalApiError(error)) {
      emitAppEvent("xphub:feedback", {
        type: "error",
        title: "No se pudo completar",
        message: apiErrorMessage(error, context),
        durationMs: 6200,
      });
    }
    return Promise.reject(error);
  }
);

export default api;
